import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { supabase } from "../lib/supabaseClient";
import { useNavigate, useLocation } from "react-router-dom";

const UserContext = createContext();

// 전역 캐시
const userCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5분

export function UserProvider({ children }) {
  const [userInfo, setUserInfo] = useState({
    email: "",
    name: "",
    class: "",
    role: "",
    company: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const lastFetchedEmail = useRef("");
  const fetchTimeoutRef = useRef(null);
  const isFetching = useRef(false);
  const lastSessionEvent = useRef(null);

  useEffect(() => {
    async function fetchUserInfoFromSession(session) {
      const impersonateEmail = localStorage.getItem("impersonateEmail");
      const targetEmail = impersonateEmail || session?.user?.email;
      
      // / 경로로 접속할 때는 캐시를 무시하고 무조건 새로운 데이터를 가져옴
      const isHomeRoute = location.pathname === "/";
      
      // 이메일이 변경되지 않았고 홈 경로가 아니면 호출하지 않음
      if (targetEmail === lastFetchedEmail.current && !isHomeRoute) {
        return;
      }
      
      // 이미 fetch 중이면 중단
      if (isFetching.current) {
        return;
      }
      
      // 세션 이벤트가 너무 자주 발생하면 무시 (1초 내)
      const now = Date.now();
      if (lastSessionEvent.current && (now - lastSessionEvent.current) < 1000) {
        return;
      }
      lastSessionEvent.current = now;
      
      // 이전 타임아웃 클리어
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      
      // 디바운싱: 500ms 후에 실행
      fetchTimeoutRef.current = setTimeout(async () => {
        // 홈 경로가 아니면 캐시 확인
        if (!isHomeRoute && targetEmail && userCache.has(targetEmail)) {
          const cached = userCache.get(targetEmail);
          if (Date.now() - cached.timestamp < CACHE_DURATION) {
            setUserInfo(cached.data);
            lastFetchedEmail.current = targetEmail;
            setIsLoading(false);
            return;
          }
        }
        
        isFetching.current = true;
        setIsLoading(true);
        lastFetchedEmail.current = targetEmail;
        
        try {
          if (targetEmail) {
            const { data } = await supabase
              .from("user")
              .select("email, name, class, role, company")
              .eq("email", targetEmail)
              .maybeSingle();
            
            if (data) {
              setUserInfo(data);
              // 홈 경로가 아니면 캐시에 저장
              if (!isHomeRoute) {
                userCache.set(targetEmail, {
                  data,
                  timestamp: Date.now()
                });
              }
            } else {
              setUserInfo({
                email: "",
                name: "",
                class: "",
                role: "",
                company: "",
              });
            }
          } else {
            setUserInfo({ email: "", name: "", class: "", role: "", company: "" });
            if (
              location.pathname !== "/signup" &&
              location.pathname !== "/login" &&
              location.pathname !== "/terms-of-service" &&
              location.pathname !== "/privacy-policy" &&
              location.pathname !== "/reset-password" &&
              location.pathname !== "/watcha-shorts"
            ) {
              navigate("/login", { replace: true });
            }
          }
        } catch (error) {
          console.error("User fetch error:", error);
        } finally {
          setIsLoading(false);
          isFetching.current = false;
        }
      }, 500);
    }

    // 세션 변경 이벤트를 한 번만 등록
    const result = supabase.auth.onAuthStateChange((_event, session) => {
      fetchUserInfoFromSession(session);
    });
    
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      isFetching.current = false;
      if (typeof result === "function") {
        result();
      } else if (result?.data?.unsubscribe) {
        result.data.unsubscribe();
      } else if (result?.unsubscribe) {
        result.unsubscribe();
      }
    };
  }, [navigate, location.pathname]);

  return (
    <UserContext.Provider value={{ userInfo, setUserInfo, isLoading }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}
